import { runInAction, action, observable, makeObservable } from "mobx";
import { createContext, useContext } from "react";

import { clipByPolygon, exportMap2PDF, getPolygonBounds, getPolygonCoords, logObject } from "./utils";
import External from "./external";

class Model {
    mapEngine = null;
    loading = false;
    drawing = false;
    modify = false;
    delete = false;
    changeColor = false;
    changeColorPolygon = null;
    addRoad = false;
    selectRoads = null;
    splitRoads = null;
    polygons = [];
    shade = false;
    exportPDF = false;
    totalHouseholds = 0;

    constructor() {
        makeObservable(this, {
            loading: observable,
            drawing: observable,
            polygons: observable,
            modify: observable,
            delete: observable,
            changeColor: observable,
            changeColorPolygon: observable,
            addRoad: observable,
            selectRoads: observable,
            splitRoads: observable,
            shade: observable,
            exportPDF: observable,
            totalHouseholds: observable,
            startDrawing: action,
            polygonCreated: action,
            startLoading: action,
            endLoading: action,
            startChangeColor: action,
            stopChangeColor: action,
            colorPolygonChanged: action,
            changeModify: action,
            deletePolygon: action,
            nextShade: action,
            endShade: action,
            setTotalHouseholds: action
        });
    }

    startDrawing() {
        this.drawing = this.mapEngine.startDrawing();
    }

    stopDrawing() {
        this.drawing = null;
        this.mapEngine.stopDrawing(this.drawing);
    }

    startLoading() {
        this.loading = true;
    }

    endLoading() {
        this.loading = false;
    }

    async getPolygonRoads(polygon) {
        const coords = getPolygonCoords(polygon);
        const result = await External.getRoads(coords);
        const clipped = clipByPolygon(result, polygon);
        return clipped;
    }

    async polygonCreated(layer, layerId) {
        this.startLoading();

        if (this.polygons.indexOf(layerId) === -1) {
            this.polygons.push(layerId);
        }

        const roads = await this.getPolygonRoads(layer);

        runInAction(() => {
            this.drawing = null;
            this.endLoading();
        });

        return roads;
    }

    startModify() {
        this.modify = this.mapEngine.startModify();
    }

    changeModify(changedFeatures) {
        const feature = changedFeatures[0];
        const index = this.modify.findIndex((item) => item.properties.id === feature.properties.id);
        this.modify[index] = feature;
    }

    async saveModify() {
        await this.mapEngine.saveModify(this.modify);
        this.modify = null;
    }

    cancelModify() {
        this.mapEngine.cancelModify(this.modify);
        this.modify = null;
    }

    startChangeColor() {
        this.changeColor = true;
        if (this.polygons.length) {
            this.colorPolygonChanged(this.polygons[0]);
        }
    }

    colorPolygonChanged(id) {
        const color = this.mapEngine.getRoadsColor(id);
        this.changeColorPolygon = { id, color };
    }

    stopChangeColor() {
        this.changeColor = false;
        this.changeColorPolygon = null;
    }

    savePolygonChangeColor(color) {
        this.mapEngine.changeRoadsColor(this.changeColorPolygon.id, color);
        this.changeColorPolygon.color = color;
    }

    exportKML() {
        this.startLoading();
        this.mapEngine.exportKML();
        this.endLoading();
    }

    startExportPDF() {
        this.exportPDF = this.mapEngine.startExportPDF();
    }

    stopExportPDF() {
        this.mapEngine.stopExportPDF();
        this.exportPDF = null;
    }

    startAddRoad() {
        this.addRoad = this.mapEngine.startAddRoad();
    }

    stopAddRoad() {
        this.mapEngine.stopAddRoad(this.addRoad);
        this.addRoad = null;
    }

    addRoadsFromLine(line, polygons) {
        const collection = { type: "FeatureCollection", features: [line] };

        polygons.forEach((layer) => {
            const clipped = clipByPolygon(collection, layer, true);
            this.mapEngine.addRoads(layer.layerId, clipped);
        });
        this.addRoad = null;
    }

    startSelectRoads() {
        this.selectRoads = this.mapEngine.startSelectRoads();
    }

    endSelectRoads() {
        this.mapEngine.endSelectRoads();
        this.selectRoads = false;
    }

    deleteSelectedRoads() {
        this.mapEngine.deleteSelectedRoads();
    }

    startSplitRoads() {
        this.splitRoads = true;
    }

    endSplitRoads() {
        this.splitRoads = false;
        this.mapEngine.endSplitRoads();
    }

    startDelete() {
        this.delete = true;
        this.mapEngine.startDelete();
    }

    deletePolygon(id) {
        const index = this.polygons.findIndex((item) => item === id);
        if (index > -1) {
            this.polygons.splice(index, 1);
        }
    }

    endDelete() {
        this.delete = false;
        this.mapEngine.endDelete();
    }
    //
    nextShade() {
        if (this.shade === false) {
            this.shade = 0;
        } else if (this.shade + 1 === this.polygons.length) {
            this.shade = 0;
        } else {
            this.shade++;
        }

        this.mapEngine.nextShade(this.polygons[this.shade]);
    }
    endShade() {
        this.shade = false;
        this.mapEngine.stopShading();
    }
    //
    addStamp(text) {
        this.mapEngine.addStamp(text);
    }

    addStart() {
        this.mapEngine.addStart();
    }

    addText() {
        this.mapEngine.addText();
    }

    setTotalHouseholds(value) {
        this.totalHouseholds = value;
    }
}

const ModelContext = createContext(new Model());

const useModel = () => {
    return useContext(ModelContext);
};

export default useModel;
